import { inventoryField, posCoord } from '../../shared/array-types.js';
import { readItemField as itemField } from '../compile/item-field-reader.js';
import { createVitalityReader } from '../compile/vitality-reader.js';
import { wallPieceFingerprint } from './wall-run-damage.js';
import { inventoryBirthPicture } from './birth-inputs-20.js';
import type { ObjectDestroyCommand } from './destroy-object.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;

/** Save the same native additions that every page draws. No encounter/member copy or server materialization. */
export function prepareDestructionSave(command: ObjectDestroyCommand, definition: (id: string) => Row | null): void {
  const vitality = createVitalityReader({ resolveInventoryInstance: () => null });
  for (const placed of [command.rubble, command.waypoint]) {
    if (!placed) continue;
    const added = command.frame.add.find(row => inventoryField(row, 'inventory_id') === placed.inventory_id);
    if (!added) throw new Error('Destruction addition is missing');
    const template = definition(placed.inventory_item_id);
    const health = record(template ? vitality(template) : null);
    const row: Row = { ...added.inventory, x_pos: posCoord(added, 'x_pos') ?? 0,
      y_pos: posCoord(added, 'y_pos') ?? 0, z_pos: posCoord(added, 'z_pos') ?? 0, equipped: '' };
    row.v_max = Number(inventoryField(added, 'v_max') ?? health?.v_max ?? 5);
    row.v_current = Number(inventoryField(added, 'v_current') ?? health?.v_current ?? row.v_max);
    row.v_temp = Number(inventoryField(added, 'v_temp') ?? health?.v_temp ?? 0);
    row.v_max_temp = Number(inventoryField(added, 'v_max_temp') ?? health?.v_max_temp ?? 0);
    const picture = template ? inventoryBirthPicture(template) : null;
    row.inventory_presentation_pic_id = inventoryField(added, 'inventory_presentation_pic_id') ?? picture;
    row.inventory_thumbnail_pic_id = inventoryField(added, 'inventory_thumbnail_pic_id') ?? row.inventory_presentation_pic_id;
    const generation = record(inventoryField(added, 'inventory_generation'));
    if (generation) {
      generation.fingerprint = wallPieceFingerprint(row);
      added.inventory.inventory_generation = generation;
    }
    placed.saved_row = row;
  }
  if (command.cause) {
    const template = definition(command.cause.ability_id);
    command.cause.items = { item_id: command.cause.ability_id, item_type: String(itemField(template, 'item_type') ?? 'ability') };
  }
  for (const child of command.spill?.destroyed ?? []) prepareDestructionSave(child, definition);
}
